import { BaseDataType } from "../base-data-type";
import { ConfDataService } from "../conf-data.service";
import { ServiceLocator } from "../service-locator";
import { FormControl, FormGroup, NgForm } from "@angular/forms";

export class Utils {

    public static readonly TAG: string = "[Utils]";

    public static confDataService: any;

    /**
     * Check if all conditions are filled, to show navigation element.
     *
     * @see {@link README.md#Conditions} for further information.
     * @param pType {BaseDataType}
     */
    public static conditionsValidated(pType: BaseDataType): boolean {
        // console.log(this.TAG, "conditionsValidated()", pType);

        if (this.confDataService === undefined) {
            this.confDataService = ServiceLocator.injector.get(ConfDataService);
        }

        // If no condition, considered as valid.
        if (!pType || !pType.conditions) {
            return true;
        }


        // Considered as valid if one of the conditions are filled.
        let conditionValidated: boolean;
        for (const condition of pType.conditions) {
            conditionValidated = true;

            // Split condition if logic inside.
            // Only '&&' condition is supported.
            const expressions: string[] = condition.split(" && ");
            for (const exp of expressions) {

                try {
                    const el: string[] = exp.split(" ");
                    if (el.length < 3) {
                        console.warn(this.TAG, "condition invalid", condition, "Must be like `variable operator variable`, ex: `project-type !== projet-goutiere`");
                        return true;
                    }

                    // Check for a specific value id, like for 'belriv-dimensions' => 'belriv-dimensions-facade-tradi-width'
                    // This conditions are written like that :
                    // "belriv-dimensions^belriv-tradi-pignon-dimension-width === 7mm"
                    // The main id is separated by the value with a '.' character.
                    let valueString = "";
                    if (el[0].indexOf(".") !== -1) {
                        valueString = el[0].split(".")[1];
                        el[0] = el[0].split(".")[0];
                    }

                    const storedValue: string = valueString !== "" ? this.confDataService.confdata[el[0]].values[0][valueString] : this.confDataService.confdata[el[0]].values[0];

                    switch (el[1]) {
                        case "!==":
                            if (storedValue !== el[2]) {
                                continue;
                            }
                            conditionValidated = false;
                            break;

                        case "===":
                            if (storedValue === el[2]) {
                                continue;
                            }
                            conditionValidated = false;
                            break;

                        case "in":
                            if (this.confDataService.confdata[el[0]].options.indexOf(el[2]) !== -1) {
                                continue;
                            }
                            conditionValidated = false;
                            break;

                        case "notin":
                            if (this.confDataService.confdata[el[0]].options.indexOf(el[2]) === -1) {
                                continue;
                            }
                            conditionValidated = false;
                            break;

                        default:
                            break;
                    }
                } catch (error) {
                    // console.log(this.TAG, "error", "conditionsValidated()", error, condition);
                    conditionValidated = false;
                }
            }

            // First validated condition returns true.
            if (conditionValidated) {
                return true;
            }
        }

        return false;
    }

    /**
     * Check if all conditions are filled, to show navigation element.
     *
     * @see {@link README.md#Conditions} for further information.
     * @param pType {BaseDataType}
     */
    public static conditionsOptionsValidated(pType: BaseDataType): boolean {
        // console.log(this.TAG, "conditionsValidated()", pType);

        if (this.confDataService === undefined) {
            this.confDataService = ServiceLocator.injector.get(ConfDataService);
        }

        // If no condition, considered as valid.
        if (!pType || !pType.conditions) {
            return true;
        }


        // Considered as valid if one of the conditions are filled.
        let conditionValidated: boolean;
        for (const condition of pType.conditions) {
            conditionValidated = true;

            // Split condition if logic inside.
            // Only '&&' condition is supported.
            const expressions: string[] = condition.split(" && ");
            for (const exp of expressions) {

                try {
                    const el: string[] = exp.split(" ");
                    if (el.length < 3) {
                        console.warn(this.TAG, "condition invalid", condition, "Must be like `variable operator variable`, ex: `project-type !== projet-goutiere`");
                        return true;
                    }

                    // Check for a specific value id, like for 'belriv-dimensions' => 'belriv-dimensions-facade-tradi-width'
                    // This conditions are written like that :
                    // "belriv-dimensions^belriv-tradi-pignon-dimension-width === 7mm"
                    // The main id is separated by the value with a '.' character.
                    let valueString = "";
                    if (el[0].indexOf(".") !== -1) {
                        valueString = el[0].split(".")[1];
                        el[0] = el[0].split(".")[0];
                    }

                    const storedOptions: any[] = this.confDataService.confdata[el[0]].options;

                    switch (el[1]) {
                        case "!==":
                            if (storedOptions.indexOf(el[2]) === -1) {
                                continue;
                            }
                            conditionValidated = false;
                            break;

                        case "===":
                            if (storedOptions.indexOf(el[2]) > -1) {
                                continue;
                            }
                            conditionValidated = false;
                            break;

                        default:
                            break;
                    }
                } catch (error) {
                    // console.log(this.TAG, "error", "conditionsValidated()", error, condition);
                    conditionValidated = false;
                }
            }

            // First validated condition returns true.
            if (conditionValidated) {
                return true;
            }
        }

        return false;
    }


    // JAR / NOT USED / Test to validate forms / Set "input" validation color to red if error / Fail
    // See https://codecraft.tv/courses/angular/advanced-topics/basic-custom-validators/ to create form validators !
    public static markAsDirty(ngForm: NgForm, pId: string) {

        Object.keys(ngForm.form.controls).map((field) => {
            if (field === pId) {
                const control = ngForm.form.get(field);
                if (control instanceof FormControl) {
                    control.setErrors({ onlySelf: false });
                }
            }
        });

    }

    public static checkFormControlsValidity(form: FormGroup): void {
        Object.keys(form.controls).map((field) => {
            const control = form.get(field);
            if (control instanceof FormControl) {
                control.markAsDirty({ onlySelf: true });
                control.markAsTouched({ onlySelf: true });
            }
        });
    }

    /**
     * Check if element is visible in the view. If not, scroll into view.
     *
     * https://stackoverflow.com/questions/487073/check-if-element-is-visible-after-scrolling
     */
    public static scrollIntoView(el: HTMLElement, scrollContainer: HTMLElement): void {
        const rect = el.getBoundingClientRect();
        const elemTop = rect.top;
        const elemBottom = rect.bottom + 40;

        // Only completely visible elements return true:
        const isVisible = (elemTop >= 0) && (elemBottom <= window.innerHeight);

        if (isVisible === false) {
            // scrollContainer.scrollBy({left: 0, top: elemBottom - window.innerHeight, behavior: "smooth"});
            console.log(this.TAG, "scrollBy is not managed by IE ...");
            scrollContainer.scrollLeft = 0;
            scrollContainer.scrollTop = elemBottom - window.innerHeight;
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/util/Utils.ts